import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Grid, List, Trash2 } from 'lucide-react';
import { Listing } from '../types';
import { getListings, deleteListing, updateListing, getThumbnailUrl } from '../services/supabase';
import ListingCard from './ListingCard';
import HdrUsageCard from './HdrUsageCard';
import type { HdrEntitlement } from '../services/hdr';

interface ListingsPageProps {
  userId: string;
  entitlement?: HdrEntitlement | null;
}

const ListingsPage: React.FC<ListingsPageProps> = ({ userId, entitlement }) => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadListings(true);
  }, [userId]);

  // Keep statuses live while jobs are running so cards don't feel "stuck".
  // Only poll if there are items still processing.
  useEffect(() => {
    const hasProcessing = listings.some(l => l.status === 'processing' || l.status === 'pending');
    if (!hasProcessing) return;
    
    const timer = setInterval(() => {
      loadListings(false); // silent refresh, no loading spinner
    }, 10000); // slower polling to reduce flicker
    return () => clearInterval(timer);
  }, [userId, listings]);

  const loadListings = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    const data = await getListings(userId);
    setListings(data);
    if (showLoading) setIsLoading(false);
  };

  const handleDelete = async (listingId: string) => {
    if (window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) {
      const success = await deleteListing(listingId);
      if (success) {
        setListings(prev => prev.filter(l => l.id !== listingId));
      }
    }
  };

  const handleRename = async (listingId: string, name: string) => {
    // Optimistic update so the card reflects the new name immediately.
    setListings(prev => prev.map(l => (l.id === listingId ? { ...l, name } : l)));
    const updated = await updateListing(listingId, { name });
    if (!updated) {
      // Revert on failure by reloading from the server.
      loadListings(false);
    }
  };

  // Filter listings
  const filteredListings = listings.filter(listing => {
    const matchesSearch = 
      listing.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Listings</h1>
          <p className="text-slate-400 mt-1">
            {listings.length} {listings.length === 1 ? 'property' : 'properties'}
          </p>
        </div>

        <Link
          to="/upload"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Listing
        </Link>
      </div>

      <div className="mb-6">
        <HdrUsageCard entitlement={entitlement ?? null} />
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-500 transition-colors"
          />
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-brand-500 transition-colors"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="ready">Ready</option>
            <option value="error">Error</option>
          </select>
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid' 
                ? 'bg-brand-500 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list' 
                ? 'bg-brand-500 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          {listings.length === 0 ? (
            <>
              <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Plus className="w-10 h-10 text-slate-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                No listings yet
              </h3>
              <p className="text-slate-400 max-w-sm mb-6">
                Upload your first property photos to get started with HDR processing.
              </p>
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                Upload Photos
              </Link>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Search className="w-10 h-10 text-slate-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                No results found
              </h3>
              <p className="text-slate-400">
                Try adjusting your search or filters.
              </p>
            </>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredListings.map((listing) => (
            <ListingCard 
              key={listing.id} 
              listing={listing}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredListings.map((listing) => (
            <Link
              key={listing.id}
              to={`/studio/${listing.id}`}
              className="flex items-center gap-4 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-brand-500/50 transition-all"
            >
              <div className="w-24 h-16 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                {listing.cover_image_url ? (
                  <img
                    src={getThumbnailUrl(listing.cover_image_url, { width: 200, height: 150, quality: 70 }) || listing.cover_image_url}
                    alt={listing.address}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Plus className="w-6 h-6 text-slate-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white truncate">
                  {listing.name || listing.address}
                </h3>
                <p className="text-sm text-slate-400 truncate">
                  {listing.media_count} images
                </p>
              </div>
              <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                listing.status === 'ready' ? 'bg-emerald-500/20 text-emerald-400' :
                listing.status === 'processing' ? 'bg-amber-500/20 text-amber-400' :
                listing.status === 'error' ? 'bg-red-500/20 text-red-400' :
                'bg-slate-700 text-slate-400'
              }`}>
                {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(listing.id);
                }}
                className="flex items-center justify-center w-9 h-9 text-slate-400 hover:text-white hover:bg-red-500 rounded-lg transition-colors flex-shrink-0"
                title="Delete listing"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListingsPage;
